import React from 'react';
import styled from '@emotion/styled';
import { theme } from 'common/theme';
import { BrandColorMapType } from 'common/types/common';
import { LatanaText } from 'common/components/LatanaText';
import Icon from 'common/components/Icon';
import AddCompetitorModal, { DEFAULT_BRAND_COLOR } from './AddCompetitorModal';
import ColorPickerInput from './ColorPickerInput';
import { useGetAllBrands } from 'common/queries/brands';
import { generateBrandsColorMap } from 'utils/brands';
import { useTranslation } from 'react-i18next';
import { useTracking } from 'common/hooks/tracking';
import { TRACKING_EVENTS } from 'common/types/tracking';

const Container = styled.div`
  padding: ${theme.space(2.25)};
  border: 1px solid ${theme.colors.borderGrey.toString()};
  border-radius: 8px;
  width: 420px;
  max-width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.space(3)};
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.space(2)};
  gap: ${theme.space(2)};
  position: relative;

  & > *:nth-of-type(3) {
    margin-left: auto;
  }
`;

const ColorDot = styled.div<{ color: string }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const AddButton = styled.button`
  width: 100%;
  padding: ${theme.space(1.5, 3)};
  background-color: ${theme.v2.colours.gray0};
  color: ${theme.v2.colours.gray900};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  display: block;
  margin: 0 auto;
  border: 1px solid ${theme.v2.colours.gray200};
  margin-bottom: ${theme.space(2)};
`;

const SaveButton = styled.button`
  width: 100%;
  padding: ${theme.space(1.5, 3)};
  background-color: ${theme.v2.colours.latana};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  display: block;
  margin: 0 auto;

  &:hover {
    background-color: ${theme.v2.colours.latana900};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.space(1)};
  color: ${theme.v2.colours.gray500};
`;

interface BrandColorSettingsProps {
  onSave: (overriddenColors: BrandColorMapType) => void;
  overriddenBrandColors?: BrandColorMapType | undefined;
}

const BrandColorSettings: React.FC<BrandColorSettingsProps> = ({ onSave, overriddenBrandColors }) => {
  const { t } = useTranslation();
  const [overriddenColors, setOverriddenColors] = React.useState<BrandColorMapType>(overriddenBrandColors || {});
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { data: allBrandsList } = useGetAllBrands();
  const { trackEvent } = useTracking();

  // Memoize brandsColorMap keys to prevent recalculation
  const defaultBrandColors = React.useMemo(() => {
    return allBrandsList ? generateBrandsColorMap(allBrandsList) : {};
  }, [allBrandsList]);

  // Memoize brandsColorMap keys to prevent recalculation
  const brandKeys = React.useMemo(() => {
    return defaultBrandColors ? Object.keys(defaultBrandColors) : [];
  }, [defaultBrandColors]);

  // Update availableBrands to use memoized keys
  const availableBrands = React.useMemo(() => {
    const selectedBrands = new Set(Object.keys(overriddenColors));
    return brandKeys.filter(brand => !selectedBrands.has(brand));
  }, [brandKeys, overriddenColors]);

  // Get the effective color for a brand (overridden or default)
  const getBrandColor = React.useCallback(
    (brandId: string) => {
      return overriddenColors[brandId] || (defaultBrandColors?.[brandId] ?? DEFAULT_BRAND_COLOR);
    },
    [overriddenColors, defaultBrandColors], // Remove brandsColorMap from dependencies
  );

  const handleAddBrand = (competitor: string, color: string) => {
    setOverriddenColors(prev => ({
      ...prev,
      [competitor]: color,
    }));
  };

  const handleColorChange = (brandId: string, color: string) => {
    setOverriddenColors(prev => ({
      ...prev,
      [brandId]: color,
    }));
  };

  const handleSave = () => {
    onSave(overriddenColors);
    trackEvent(TRACKING_EVENTS.SETTINGS_BRAND_COLOR_SETTINGS_SAVE);
  };

  const handleDelete = (brandId: string) => {
    const { [brandId]: _, ...rest } = overriddenColors;
    setOverriddenColors(rest);
  };

  return (
    <Container>
      <Header>
        <div>
          <LatanaText variant='T1' color='gray900'>
            {t('appearance.brandColorSettings')}
          </LatanaText>
          <LatanaText variant='B2' color='gray900'>
            {t('appearance.brandColorDescription')}
          </LatanaText>
        </div>
      </Header>

      {Object.keys(overriddenColors)
        .sort()
        .map(brandId => (
          <BrandRow key={brandId}>
            <ColorDot color={getBrandColor(brandId)} />
            <LatanaText variant='B1'>{brandId}</LatanaText>
            <ColorPickerInput color={getBrandColor(brandId)} onChange={color => handleColorChange(brandId, color)} />
            <DeleteButton onClick={() => handleDelete(brandId)}>
              <Icon icon='trash-can' size={16} />
            </DeleteButton>
          </BrandRow>
        ))}

      <AddButton onClick={() => setIsModalOpen(true)}>{t('appearance.addCompetitorBrand')}</AddButton>

      <AddCompetitorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddBrand}
        availableBrands={availableBrands}
        defaultBrandColors={defaultBrandColors || {}}
      />

      <SaveButton onClick={handleSave}>{t('appearance.saveColorSettings')}</SaveButton>
    </Container>
  );
};

export default React.memo(BrandColorSettings);
